// @flow
import * as React from 'react';

import makeStyles from '@material-ui/core/styles/makeStyles';
import {MuiThemeProvider, createTheme} from '@material-ui/core/styles';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import {colors} from '../../assets/styles/colors';

//$FlowFixMe
import dayJsUtils from '@date-io/dayjs';

import CalendarIcon from '../icons/CalendarIcon';
import type {KeyboardDatePickerProps} from '@material-ui/pickers';
import type {InputProps} from '@material-ui/core';
type Props = {
  className?: string,
  label?: React.Node,
  InputProps?: InputProps,
  position?: string,
  value?: Date | null,
  handleChange: () => void,
} & KeyboardDatePickerProps;

const useStyles = makeStyles(theme => ({
  root: {
    overflow: 'inherit',
    borderRadius: '.75rem',
    border: `1.25px solid ${colors.grey300}`,
    boxSizing: 'border-box',
    backgroundColor: 'white !important',
    color: `${colors.grey800} !important`,
    transition: theme.transitions.create(['border-color', 'box-shadow']),

    '& input': {
      paddingTop: '1rem !important',
    },
  },
  input: {padding: '10px 8px !important'},

  textInputLabel: {
    color: colors.grey600,
  },
  textInput: {
    borderRadius: '1rem',
    border: `1.25px solid ${colors.grey300}`,
    boxSizing: 'border-box',
    backgroundColor: 'white !important',
    color: colors.grey800,
    flexWrap: 'wrap',
    '& .MuiFilledInput-input': {
      padding: '15px',
    },
    '& .MuiInputBase-input.Mui-disabled': {
      backgroundColor: colors.grey200,
    },
    '&.Mui-error': {
      border: `1px solid #f44336`,
    },
    '&.Mui-focused': {
      border: `1px solid ${colors.grey500} `,
      backgroundColor: 'white ',
      color: `${colors.grey800} `,
    },
  },
  iconButton: {
    color: `${colors.purple} `,
  },
}));

const theme = createTheme({
  palette: {
    background: {
      default: '#FFFFFF',
    },
    primary: {
      main: colors.purple3,
    },
  },
  overrides: {
    MuiInputBase: {
      root: {
        backgroundColor: 'white !important',
        border: `1px solid ${colors.grey300} `,
        padding: '0px 8px !important',

        '&$error': {
          border: '1.25px solid #f44336 !important',
        },
        '&$focused': {
          border: `1px solid ${colors.grey500} `,
          backgroundColor: 'white',
          color: `${colors.grey800} `,
        },
        '&$disabled': {
          backgroundColor: `${colors.grey200} !important`,
        },
      },
      input: {
        color: `${colors.grey800} `,
      },
    },
    MuiFormLabel: {
      root: {
        lineHeight: '0.3',
        color: `${colors.grey600} !important`,
        '&$error': {
          color: '#f44336 !important',
        },
      },
    },
  },
  typography: {
    fontFamily: '"Poppins", "Muli", sans-serif',
  },
});

export default function DatePicker(props: Props) {
  const {
    label,
    className,
    value,
    handleChange,
    InputProps,
    position = 'start',
    ...otherProps
  } = props;

  const classes = useStyles();

  if (label) {
    return (
      <MuiPickersUtilsProvider utils={dayJsUtils}>
        <MuiThemeProvider theme={theme}>
          <KeyboardDatePicker
            autoOk
            value={value}
            label={label}
            className={`${className ? className : ''}`}
            inputVariant="filled"
            format="DD/MM/YYYY"
            coloricon={colors.purple}
            InputAdornmentProps={{position: position}}
            InputProps={{
              ...InputProps,
              className: classes.root,
              disableUnderline: true,
            }}
            KeyboardButtonProps={{
              'aria-label': 'change date',
            }}
            keyboardIcon={
              <div size="small" className={classes.iconButton}>
                <CalendarIcon />
              </div>
            }
            invalidDateMessage={'Formato de fecha inválida'}
            onChange={handleChange}
            {...otherProps}
          />
        </MuiThemeProvider>
      </MuiPickersUtilsProvider>
    );
  }

  return (
    <MuiPickersUtilsProvider utils={dayJsUtils}>
      <KeyboardDatePicker
        autoOk
        value={value}
        label={label}
        className={`${classes.textField} ${className ? className : ''}`}
        inputVariant="outlined"
        format="DD/MM/YYYY"
        InputAdornmentProps={{position: position}}
        InputProps={{
          ...InputProps,
          className: classes.textInput,
          disableUnderline: true,
        }}
        KeyboardButtonProps={{
          'aria-label': 'change date',
        }}
        keyboardIcon={
          <div size="small" className={classes.iconButton}>
            <CalendarIcon />
          </div>
        }
        invalidDateMessage={'Formato de fecha inválida'}
        onChange={handleChange}
        {...otherProps}
      />
    </MuiPickersUtilsProvider>
  );
}
